import {useEffect, useRef, useState} from "react";
import {Alert, Box, Button, CircularProgress, TextField, Typography} from "@mui/material";
import CameraAltRoundedIcon from "@mui/icons-material/CameraAltRounded";
import {Navigate, useNavigate} from "react-router-dom";
import {flushSync} from "react-dom";
import KioskHeader from "../components/KioskHeader";
import KioskSteps from "../components/KioskSteps";
import {useKioskLang, useKioskSession} from "../context/KioskSessionContext";
import {cancelKioskOrder, getKioskOrderStatus, payKioskOrderWithLine} from "../api/kioskOrders";
import {useGlobalOrder} from "../../utils/GlobalStateContext";

const isLinePayCode = (value) => /^\d{16,19}$/.test(String(value).trim());

export default function LinePayPage({systemData, online, onHome}) {
  const navigate = useNavigate();
  const {state, dispatch, clearRecovery} = useKioskSession();
  const {tr} = useKioskLang();
  const {BarcodeScanner, barcode, setBarcode} = useGlobalOrder();
  const videoRef = useRef(null);
  const submittingRef = useRef(false);
  const lastCodeRef = useRef({code: "", at: 0});
  const streamRef = useRef(null);
  const [manualCode, setManualCode] = useState("");
  const [cameraError, setCameraError] = useState("");
  const [cameraNonce, setCameraNonce] = useState(0);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setBarcode("");
    let cancelled = false;
    let stream;
    let timer;
    const start = async () => {
      setCameraError("");
      try {
        //改用後置鏡頭：前鏡頭定焦且解析度低，條碼要貼很近才感應得到；後鏡頭有自動對焦，隔一段距離也能讀（沒有後鏡頭時自動退回前鏡頭）
        stream = await navigator.mediaDevices.getUserMedia({video: {facingMode: {ideal: "environment"}, width: {ideal: 1920}, height: {ideal: 1080}}, audio: false});
        if (cancelled) return stream.getTracks().forEach((track) => track.stop());
        streamRef.current = stream;
        //相機被系統收回（iPad 休眠、權限被撤銷等）時 track 會靜默 ended，畫面黑掉卡住：提示重新啟用
        stream.getTracks().forEach((track) => {
          track.onended = () => {
            if (!cancelled) setCameraError(tr("cameraStopped"));
          };
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        if (globalThis.BarcodeDetector) {
          const detector = new globalThis.BarcodeDetector({formats: ["code_128", "qr_code"]});
          timer = setInterval(async () => {
            if (!videoRef.current || submittingRef.current) return;
            try {
              const results = await detector.detect(videoRef.current);
              if (results[0]?.rawValue) setBarcode(results[0].rawValue);
            } catch {}
          }, 350);
        }
      } catch (error) {
        if (!cancelled) setCameraError(error?.name === "NotAllowedError" ? tr("cameraDenied") : tr("cameraUnavailable"));
      }
    };
    start();
    return () => {
      cancelled = true;
      clearInterval(timer);
      stream?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setBarcode("");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setBarcode, cameraNonce]);

  //相機權限重新開啟或 iPad 喚醒時自動重啟相機，不用等客人按重試
  useEffect(() => {
    const restartIfDead = () => {
      if (document.visibilityState !== "visible") return;
      const alive = streamRef.current?.getTracks().some((track) => track.readyState === "live");
      if (!alive) setCameraNonce((nonce) => nonce + 1);
    };
    document.addEventListener("visibilitychange", restartIfDead);
    let permissionStatus;
    const onPermissionChange = () => {
      if (permissionStatus?.state === "granted") setCameraNonce((nonce) => nonce + 1);
    };
    //Safari 不支援 query camera 時直接略過（仍有 visibilitychange 與手動重試）
    navigator.permissions?.query?.({name: "camera"}).then((status) => {
      permissionStatus = status;
      status.addEventListener("change", onPermissionChange);
    }).catch(() => {});
    return () => {
      document.removeEventListener("visibilitychange", restartIfDead);
      permissionStatus?.removeEventListener("change", onPermissionChange);
    };
  }, []);

  const recoverStatus = async () => {
    const status = await getKioskOrderStatus({orderId: state.serverOrderId, sessionId: state.sessionId});
    if (status.error === 0 && status.order.payment_status === "paid") {
      flushSync(() => dispatch({type: "SET_PAYMENT_STATUS", status: "paid", stage: "completed"}));
      navigate("/kiosk/complete", {replace: true});
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (state.paymentStatus !== "recovering" || !state.serverOrderId) return;
    setSubmitting(true);
    recoverStatus()
      .then((paid) => {
        if (!paid) {
          dispatch({type: "SET_PAYMENT_STATUS", status: "pending", stage: "awaiting_line_pay"});
          setMessage(tr("payRecovered"));
        }
      })
      .catch(() => setMessage(tr("payRecoverFailed")))
      .finally(() => setSubmitting(false));
  }, [state.paymentStatus, state.serverOrderId]);

  const submitCode = async (rawCode) => {
    const code = String(rawCode).trim().replace(/^0(?=\d{16,19}$)/, "");
    if (!isLinePayCode(code)) {
      setMessage(tr("invalidLineCode"));
      return;
    }
    const now = Date.now();
    if (submittingRef.current || (lastCodeRef.current.code === code && now - lastCodeRef.current.at < 5000)) return;
    submittingRef.current = true;
    lastCodeRef.current = {code, at: now};
    setSubmitting(true);
    setMessage("");
    try {
      const response = await payKioskOrderWithLine({orderId: state.serverOrderId, sessionId: state.sessionId, lineCode: code});
      if (response.error === 0 && response.order.payment_status === "paid") {
        flushSync(() => dispatch({type: "SET_PAYMENT_STATUS", status: "paid", stage: "completed"}));
        navigate("/kiosk/complete", {replace: true});
      } else {
        setMessage(response.msg || tr("payIncomplete"));
      }
    } catch {
      setMessage(tr("payChecking"));
      try {
        const paid = await recoverStatus();
        if (!paid) setMessage(tr("payUnconfirmed"));
      } catch {
        setMessage(tr("payNetworkError"));
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
      setBarcode("");
    }
  };

  useEffect(() => {
    if (barcode) submitCode(barcode);
  }, [barcode]);

  //返回重選付款方式：先取消這筆未付款訂單再清掉本機狀態，避免重新送單後 POS 出現兩筆相同訂單
  //（取消失敗也繼續返回，後端建新單時會自動取消同 session 的未付款舊單）
  const backToPayment = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await cancelKioskOrder({orderId: state.serverOrderId, sessionId: state.sessionId});
    } catch {}
    clearRecovery();
    flushSync(() => dispatch({type: "CLEAR_SERVER_ORDER"}));
    navigate("/kiosk/payment");
  };

  if (!state.serverOrderId || state.paymentMethod !== "line_pay") return <Navigate to="/kiosk/payment" replace/>;

  return <Box sx={{height: "100dvh", display: "flex", flexDirection: "column"}}>
    <BarcodeScanner/>
    <KioskHeader systemData={systemData} onHome={onHome} method={state.orderMethod} tableNumber={state.tableNumber} online={online}/>
    <KioskSteps active={2}/>
    <Box sx={{flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: "1fr 430px", gap: 3, p: 3}}>
      <Box sx={{position: "relative", overflow: "hidden", borderRadius: 2, bgcolor: "#1F1A17", display: "grid", placeItems: "center"}}>
        <Box component="video" ref={videoRef} playsInline muted sx={{width: "100%", height: "100%", objectFit: "cover"}}/>
        <Box sx={{position: "absolute", width: "74%", height: "34%", border: "5px solid #FFF", borderRadius: 4, boxShadow: "0 0 0 999px rgba(0,0,0,.38)"}}/>
        {submitting && <Box sx={{position: "absolute", inset: 0, bgcolor: "rgba(30,20,15,.78)", display: "grid", placeItems: "center", color: "#FFF"}}><Box sx={{textAlign: "center"}}><CircularProgress color="inherit" size={70}/><Typography sx={{fontSize: 28, fontWeight: 900, mt: 2}}>{tr("paying")}</Typography></Box></Box>}
      </Box>
      <Box sx={{p: 3, bgcolor: "#FFF", borderRadius: 2, border: "1px solid rgba(80,55,40,.12)", display: "flex", flexDirection: "column"}}>
        <CameraAltRoundedIcon color="primary" sx={{fontSize: 54}}/>
        <Typography sx={{fontSize: 30, fontWeight: 950, mt: 1}}>{tr("scanLinePay")}</Typography>
        <Typography color="text.secondary" sx={{fontSize: 18, mt: 1}}>{tr("scanLinePayHint")}</Typography>
        <Typography sx={{fontSize: 22, fontWeight: 900, mt: 3}}>{tr("payAmount", {n: Number(state.pricing?.total ?? 0).toLocaleString()})}</Typography>
        {cameraError && <Alert severity="warning" sx={{mt: 2, alignItems: "center"}} action={<Button color="inherit" size="small" onClick={() => setCameraNonce((nonce) => nonce + 1)} sx={{fontWeight: 900, whiteSpace: "nowrap"}}>{tr("cameraRetry")}</Button>}>{cameraError}</Alert>}
        {message && <Alert severity="error" sx={{mt: 2}}>{message}</Alert>}
        <Box sx={{mt: "auto"}}>
          <Typography color="text.secondary" sx={{mb: 1}}>{tr("manualCodeHint")}</Typography>
          <Box sx={{display: "flex", gap: 1}}><TextField value={manualCode} onChange={(event) => setManualCode(event.target.value.replace(/\D/g, "").slice(0, 19))} inputMode="numeric" fullWidth/><Button variant="outlined" onClick={() => submitCode(manualCode)} disabled={submitting}>{tr("confirm")}</Button></Box>
          <Button fullWidth onClick={backToPayment} disabled={submitting} sx={{mt: 2}}>{tr("backToPayment")}</Button>
        </Box>
      </Box>
    </Box>
  </Box>;
}
